import React, { useEffect, useMemo, useState } from "react";

export default function ChooseScreen({ goBrowse, goCreateStore, goBack }) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 820 : false
  );

  // Only used for hover effects on desktop
  const [hovered, setHovered] = useState(""); // "browse" | "store" | ""

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const S = useMemo(() => {
    const gridCols = isMobile ? "1fr" : "1fr 1fr";
    const gridGap = isMobile ? 16 : 26;

    const baseShadow = "0 18px 45px rgba(15,23,42,0.08)";
    const hoverShadow = "0 30px 70px rgba(15,23,42,0.16)";

    const cardBase = {
      background: "#ffffff",
      borderRadius: 28,
      padding: isMobile ? 18 : 28,
      border: "1px solid #e5e7eb",
      boxShadow: baseShadow,
      cursor: "pointer",
      transition:
        "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      overflow: "hidden",
      position: "relative",
      minHeight: isMobile ? "auto" : 560,
      outline: "none",
    };

    const badgeBase = {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 12px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 1000,
      border: "1px solid #e2e8f0",
      background: "#ffffff",
      color: "#0f172a",
      width: "fit-content",
    };

    const pillBlue = {
      background: "#eff6ff",
      borderColor: "#bfdbfe",
      color: "#1d4ed8",
    };
    const pillGreen = {
      background: "#ecfdf5",
      borderColor: "#bbf7d0",
      color: "#047857",
    };

    const btnBase = {
      padding: isMobile ? "12px 14px" : "14px 16px",
      borderRadius: 14,
      fontWeight: 1000,
      fontSize: 14,
      cursor: "pointer",
      transition:
        "transform 180ms ease, box-shadow 180ms ease, background 180ms ease",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      width: "100%",
    };

    return {
      page: {
        minHeight: "100vh",
        background: "#f8fafc",
        color: "#0f172a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? 18 : 28,
      },

      wrap: { width: "100%", maxWidth: 1100 },

      topBar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: isMobile ? 16 : 20,
      },

      brand: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontWeight: 1100,
        letterSpacing: "-0.02em",
      },

      brandDot: {
        width: 12,
        height: 12,
        borderRadius: 999,
        background: "#0f172a",
        boxShadow: "0 10px 20px rgba(15,23,42,0.15)",
      },

      stepWrap: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        borderRadius: 999,
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        boxShadow: "0 10px 25px rgba(15,23,42,0.06)",
      },

      stepNum: {
        width: 26,
        height: 26,
        borderRadius: 999,
        background: "#0f172a",
        color: "#fff",
        display: "grid",
        placeItems: "center",
        fontWeight: 1100,
        fontSize: 12,
      },

      stepText: { fontSize: 12, color: "#334155", fontWeight: 900 },

      header: {
        textAlign: "center",
        marginBottom: isMobile ? 18 : 28,
      },

      title: {
        fontSize: isMobile ? 28 : 38,
        fontWeight: 1100,
        letterSpacing: "-0.03em",
        marginBottom: 10,
      },

      sub: {
        fontSize: 16,
        color: "#475569",
        lineHeight: 1.7,
        maxWidth: 680,
        margin: "0 auto",
      },

      highlights: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        flexWrap: "wrap",
        marginTop: 16,
      },

      highlightPill: {
        padding: "10px 14px",
        borderRadius: 999,
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        fontSize: 12,
        fontWeight: 1000,
        color: "#0f172a",
        boxShadow: "0 10px 25px rgba(15,23,42,0.06)",
      },

      grid: {
        display: "grid",
        gridTemplateColumns: gridCols,
        gap: gridGap,
        alignItems: "stretch",
      },

      card: cardBase,
      cardHover: {
        transform: "translateY(-6px)",
        boxShadow: hoverShadow,
        borderColor: "#cbd5e1",
      },

      cardTopRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      },

      badge: badgeBase,
      badgeBlue: { ...badgeBase, ...pillBlue },
      badgeGreen: { ...badgeBase, ...pillGreen },

      artArea: {
        width: "100%",
        borderRadius: 22,
        background: "linear-gradient(135deg, #f1f5f9, #ffffff)",
        border: "1px solid #eef2f7",
        padding: isMobile ? 14 : 18,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },

      artImg: {
        width: "100%",
        maxWidth: isMobile ? 260 : 340,
        height: "auto",
        display: "block",
      },

      cardTitle: {
        fontSize: 22,
        fontWeight: 1100,
        letterSpacing: "-0.02em",
      },

      cardSub: {
        fontSize: 14,
        color: "#64748b",
        lineHeight: 1.7,
        marginTop: 2,
      },

      list: {
        display: "grid",
        gap: 10,
        marginTop: 6,
      },

      li: {
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        fontSize: 13,
        color: "#0f172a",
      },

      check: {
        width: 18,
        height: 18,
        borderRadius: 6,
        background: "#0f172a",
        color: "#fff",
        display: "grid",
        placeItems: "center",
        fontSize: 12,
        flexShrink: 0,
        marginTop: 1,
      },

      btnPrimary: {
        ...btnBase,
        background: "#0f172a",
        color: "#fff",
        border: "none",
        boxShadow: "0 14px 30px rgba(15,23,42,0.18)",
      },

      btnOutline: {
        ...btnBase,
        background: "#ffffff",
        color: "#0f172a",
        border: "1px solid #cbd5e1",
        boxShadow: "0 14px 30px rgba(15,23,42,0.06)",
      },

      btnHint: {
        fontSize: 12,
        color: "#64748b",
        marginTop: 10,
        textAlign: "center",
        lineHeight: 1.5,
      },

      footer: {
        marginTop: isMobile ? 18 : 26,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
        paddingTop: 14,
        borderTop: "1px solid #e2e8f0",
      },

      footerLeft: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      },

      linkBtn: {
        background: "none",
        border: "none",
        color: "#0f172a",
        fontWeight: 1000,
        cursor: "pointer",
        padding: 0,
      },

      muted: { color: "#64748b", fontSize: 12, fontWeight: 900 },

      stats: {
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
      },

      statChip: {
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 999,
        padding: "10px 12px",
        fontSize: 12,
        fontWeight: 1000,
        color: "#0f172a",
        boxShadow: "0 10px 25px rgba(15,23,42,0.05)",
      },
    };
  }, [isMobile]);

  // hover only for desktop
  const applyHover = (key) => {
    if (isMobile) return;
    setHovered(key);
  };
  const clearHover = () => {
    if (isMobile) return;
    setHovered("");
  };

  const onCardKeyDown = (e, action) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action();
    }
  };

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        {/* TOP BAR */}
        <div style={S.topBar}>
          <div style={S.brand}>
            <span style={S.brandDot} />
            <span>Lirivelle</span>
          </div>

          <div style={S.stepWrap}>
            <div style={S.stepNum}>1</div>
            <div style={S.stepText}>Choose how you want to use the platform</div>
          </div>
        </div>

        {/* HEADER */}
        <div style={S.header}>
          <div style={S.title}>Pick your path</div>
          <div style={S.sub}>
            Browse available stores and products, or create your own store and
            manage everything yourself. Payments and delivery are controlled by
            the seller.
          </div>

          <div style={S.highlights}>
            <div style={S.highlightPill}>✨ Clean & modern</div>
            <div style={S.highlightPill}>⚡ Start fast</div>
            <div style={S.highlightPill}>💳 Seller chooses payment</div>
            <div style={S.highlightPill}>🚚 Seller handles delivery</div>
          </div>
        </div>

        {/* CARDS */}
        <div style={S.grid}>
          {/* ✅ CREATE STORE (FIRST) */}
          <div
            role="button"
            tabIndex={0}
            style={{
              ...S.card,
              ...(hovered === "store" ? S.cardHover : null),
            }}
            onClick={goCreateStore}
            onMouseEnter={() => applyHover("store")}
            onMouseLeave={clearHover}
            onKeyDown={(e) => onCardKeyDown(e, goCreateStore)}
          >
            <div style={S.cardTopRow}>
              <div style={S.badgeGreen}>🏪 Seller mode</div>
              <div style={S.badge}>Full control</div>
            </div>

            <div style={S.artArea}>
              <img
                src="/illustrations/store.png"
                alt="Create store"
                style={S.artImg}
              />
            </div>

            <div>
              <div style={S.cardTitle}>Create Your Store</div>
              <div style={S.cardSub}>
                Open your store and control everything: products, pricing, payment
                method, and delivery method.
              </div>
            </div>

            <div style={S.list}>
              <div style={S.li}>
                <div style={S.check}>✓</div>
                <div>Add your own payment method (MoMo, bank, etc.)</div>
              </div>
              <div style={S.li}>
                <div style={S.check}>✓</div>
                <div>Set your own delivery method (pickup, rider, meet-up)</div>
              </div>
              <div style={S.li}>
                <div style={S.check}>✓</div>
                <div>Upload and manage your products anytime</div>
              </div>
              <div style={S.li}>
                <div style={S.check}>✓</div>
                <div>Control your store details (banner, bio, prices)</div>
              </div>
            </div>

            <button
              style={S.btnPrimary}
              onClick={(e) => {
                e.stopPropagation();
                goCreateStore();
              }}
              onMouseDown={(e) =>
                (e.currentTarget.style.transform = "scale(0.99)")
              }
              onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              Create my store →
            </button>

            <div style={S.btnHint}>
              Best if you’re ready to sell and want your own store page link.
            </div>
          </div>

          {/* ✅ BROWSE (SECOND) */}
          <div
            role="button"
            tabIndex={0}
            style={{
              ...S.card,
              ...(hovered === "browse" ? S.cardHover : null),
            }}
            onClick={goBrowse}
            onMouseEnter={() => applyHover("browse")}
            onMouseLeave={clearHover}
            onKeyDown={(e) => onCardKeyDown(e, goBrowse)}
          >
            <div style={S.cardTopRow}>
              <div style={S.badgeBlue}>👀 Buyer mode</div>
              <div style={S.badge}>Explore without a store</div>
            </div>

            <div style={S.artArea}>
              <img
                src="/illustrations/shop.png"
                alt="Browse stores"
                style={S.artImg}
              />
            </div>

            <div>
              <div style={S.cardTitle}>Browse Stores</div>
              <div style={S.cardSub}>
                Explore stores and products. Check details and use the seller’s
                listed contact/payment options.
              </div>
            </div>

            <div style={S.list}>
              <div style={S.li}>
                <div style={S.check}>✓</div>
                <div>Browse stores and see what they sell</div>
              </div>
              <div style={S.li}>
                <div style={S.check}>✓</div>
                <div>View prices, product info, and store details</div>
              </div>
              <div style={S.li}>
                <div style={S.check}>✓</div>
                <div>Use the seller’s preferred payment option (they decide)</div>
              </div>
              <div style={S.li}>
                <div style={S.check}>✓</div>
                <div>Delivery is handled by the seller (their rules)</div>
              </div>
            </div>

            <button
              style={S.btnOutline}
              onClick={(e) => {
                e.stopPropagation();
                goBrowse();
              }}
              onMouseDown={(e) =>
                (e.currentTarget.style.transform = "scale(0.99)")
              }
              onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              Explore stores →
            </button>

            <div style={S.btnHint}>
              Best if you want to browse first and decide later if you want to
              sell.
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div style={S.footer}>
          <div style={S.footerLeft}>
            <button style={S.linkBtn} onClick={goBack}>
              ← Back
            </button>
            <div style={S.muted}>
              You can create a store anytime later from your account.
            </div>
          </div>

          <div style={S.stats}>
            <div style={S.statChip}>💳 Seller controls payment</div>
            <div style={S.statChip}>🚚 Seller controls delivery</div>
            <div style={S.statChip}>🛍️ Stores + products</div>
          </div>
        </div>
      </div>
    </div>
  );
}
